import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useAnalyticsOrgLocations, TabbedTable } from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { emptyStateGraphics, TableColumnConfig, useAlert } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../../hooks/use-show-bulk-message-button';
import { queryKeys } from '../../../query-keys';
import { trackingIds } from '../../../tracking-ids';
import { drillDownTableSize } from '../../../views/common-styles';
import { InlineStats } from '../../inline-stats';
import { BulkMessageButton } from '../bulk-message-button';
import { isPatientScheduled } from '../helpers/is-patient-scheduled';
import { collectPatientIds, deriveExportValue } from '../helpers/patients';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { useSubViewTableColConfig } from '../helpers/subview-table-col-config';
import { usePatientsLastContacted } from '../hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from '../hooks/use-practice-analytics-store';
import { RestorativeTreatmentMetric } from '../restorative-treatment-metric';
import { SmallModePatientsList } from '../small-mode-patients-list';

type Tab = 'diagnosed' | 'unscheduled';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      restorativeTreatmentPlan: treatmentPlan(treatmentPlanType: RESTORATIVE) {
        details(start: $start, end: $end, step: $step) {
          patients {
            id
            diagnosed
            procedures {
              adaCode
              date
              procedureAmount
            }
            productionAmount
            FirstName: firstName
            LastName: lastName
            PMID: pmid
            MobilePhone: mobilePhone
            HomePhone: homePhone
            WorkPhone: workPhone
            Email: email
            PreferredName: preferredName
            Birthdate: birthdate
            Gender: gender
          }
        }
        totals(start: $start, end: $end, step: $step) {
          accepted
          acceptedPercent
          diagnosed
          diagnosedPercent
          qualifiedVisits
          unscheduledTreatment
        }
      }
    }
  }
`;

type CategorizedTreatment = {
  diagnosed: PracticeAnalyticsTypes.PatientInfo[];
  unscheduled: PracticeAnalyticsTypes.PatientInfo[];
};

export const RestorativeTreatmentSubView: FC<React.PropsWithChildren<PracticeAnalyticsTypes.MetricSubViewProps>> = ({
  exportEnabled,
  onPatientsLoad,
}) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { isSmallMode } = useIsSmallMode();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const isBulkMessagingEnabled = useShowBulkMessageButton();

  const [categorizedData, setCategorizedData] = useState<CategorizedTreatment>({
    diagnosed: [],
    unscheduled: [],
  });

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(
      `restorativeTreatmentSubView-${isDemoAccount}-${JSON.stringify(filters)}`
    ),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.RestorativeTreatmentPlanResponse>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.restorativeTreatmentPlan : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const processedData = useMemo(() => {
    return {
      aggregatedData: PracticeAnalyticsAggregations.restorativeTreatmentPlanDetails(data),
      patientIds: Object.values(data?.data || {}).reduce((acc, locationDetails) => {
        return collectPatientIds(locationDetails?.location?.restorativeTreatmentPlan?.details?.patients, acc);
      }, [] as string[]),
    };
  }, [data]);

  const { isLoading: isLoadingLastContacted, lastContactedDates } = usePatientsLastContacted({
    patientIds: processedData.patientIds,
  });

  useEffect(() => {
    const patients = processedData.aggregatedData.restorativeTreatmentPlan.details?.patients || [];

    setCategorizedData(
      patients.reduce(
        (final, patient) => {
          let value = { ...final, diagnosed: [...final.diagnosed, patient] };
          if (!isPatientScheduled(patient)) {
            value = { ...value, unscheduled: [...value.unscheduled, patient] };
          }
          return value;
        },
        { diagnosed: [], unscheduled: [] } as CategorizedTreatment
      )
    );
    onPatientsLoad?.(patients);
  }, [onPatientsLoad, processedData]);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'Diagnosed',
          'Last Contacted',
          '$ Value',
          'Procedure Codes',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            lastContactedDates,
            locationNames,
          }),
        sortColumn: 'productionAmount',
      }),
    [filters.locations, lastContactedDates, locationNames]
  );

  const tabs = useMemo(
    () => ({
      diagnosed: {
        data: categorizedData.diagnosed,
        label: t('Diagnosed'),
        trackingId: trackingIds.practiceAnalytics.restorativeTreatmentDiagnosedTab,
      },
      unscheduled: {
        data: categorizedData.unscheduled,
        label: t('Unscheduled'),
        trackingId: trackingIds.practiceAnalytics.restorativeTreatmentUnscheduledTab,
      },
    }),
    [categorizedData, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: 'Restorative Treatment Patients', // Translation not needed
    initialTab: 'diagnosed',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled: exportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: PracticeAnalyticsTypes.LargeModeTableProps<Tab> = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
  };

  return (
    <>
      <InlineStats
        data={[
          isSmallMode
            ? null
            : {
                key: 'diagnosedPercent',
                name: t('Patients Diagnosed'),
                value: formatters.percent(
                  processedData.aggregatedData.restorativeTreatmentPlan.totals.diagnosedPercent || 0
                ),
              },
          {
            key: 'acceptedPercent',
            name: t('Patients Accepted'),
            value: formatters.percent(
              processedData.aggregatedData.restorativeTreatmentPlan.totals.acceptedPercent || 0
            ),
          },
          {
            key: 'unscheduledTreatment',
            name: t('Unscheduled Treatment'),
            value: formatters.currency(
              Math.round(processedData.aggregatedData.restorativeTreatmentPlan.totals.unscheduledTreatment || 0)
            ),
          },
        ]}
        isDemoAccount={showDemoChipAndBanner}
        isLoading={isLoading}
      />
      <RestorativeTreatmentMetric clickNoop isDrillDownPage />
      {isSmallMode ? (
        <SmallModePatientsList<Tab>
          {...smallModeTableProps}
          trackingIdBase='restorative-treatment-patients-small-mode'
        />
      ) : (
        <LargeModeTable {...largeModeTableProps} />
      )}
    </>
  );
};

const LargeModeTable = ({
  isBulkMessagingEnabled,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  lastContactedDates,
  tabs,
  ...rest
}: PracticeAnalyticsTypes.LargeModeTableProps<Tab>) => {
  const { t } = useTranslation('analytics');

  const middleColumns: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = [
    {
      Header: t('Diagnosed'),
      headerLabel: t('Diagnosed'),
      accessor: ({ diagnosed }) => formatters.date(diagnosed),
      id: 'diagnosed',
      sortType: 'date',
      sortInverted: true,
      width: 200,
    },
  ];

  const tabsCommonConfig = {
    colConfig: useSubViewTableColConfig({
      actionsTrackingIdBase: 'restorative-treatment-patient',
      hasProcedures: true,
      middleColumns,
      patientsLastContacted: {
        isLoading: isLoadingLastContacted,
        lastContactedDates,
      },
    }),
    emptyStateConfig: 'users' as keyof typeof emptyStateGraphics,
    tableInstanceId: 'restorativePracticeSubView',
  };

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      {...rest}
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      showExportIcon={isExportEnabled}
      tabs={{
        diagnosed: {
          ...tabsCommonConfig,
          ...tabs.diagnosed,
          tableInstanceId: 'all-diagnosed-patients',
        },
        unscheduled: {
          ...tabsCommonConfig,
          ...tabs.unscheduled,
          tableInstanceId: 'unscheduled-patients',
        },
      }}
      trackingIdBase='restorative-treatment-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
